// Product types for VIDA Products system

export const PRODUCT_TYPES = ['simple', 'composite', 'collaborative'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const TIPO_PRODUCTO = [
  'VACA',
  'VALUE_FOR_MONEY',
  'LOW_COST',
  'GANCHO',
  'ESTRATEGICO',
  'SOCIAL_RELLENO',
  'SOCIAL_SBSS',
] as const;
export type TipoProducto = (typeof TIPO_PRODUCTO)[number];

export const TIPO_PRODUCTO_LABELS: Record<string, string> = {
  VACA: 'Vaca (Alta rentabilidad)',
  VALUE_FOR_MONEY: 'Value for Money',
  LOW_COST: 'Low Cost',
  GANCHO: 'Gancho',
  ESTRATEGICO: 'Estratégico',
  SOCIAL_RELLENO: 'Social Relleno',
  SOCIAL_SBSS: 'Social SBSS',
};

export const PRODUCT_STATUSES = ['draft', 'active', 'inactive', 'archived'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export interface ImageWithMetadata {
  id: string;
  src: string;
  url: string;
  etiqueta: string;
  referencia: string;
  orden: number;
  enlace: string;
  tipo: 'archivo' | 'enlace' | 'youtube' | 'vimeo';
  temporada: 'general' | 'primavera' | 'verano' | 'otoño' | 'invierno';
}

export interface EnlaceExterno {
  id: string;
  tipo: 'tienda' | 'catalogo' | 'landing';
  url: string;
  activo: boolean;
}

export const LANDING_TEMPLATES = ['product', 'service', 'corporate'] as const;
export type LandingTemplate = (typeof LANDING_TEMPLATES)[number];

/** Visual styles per template (keep in sync with backend + landing frontend). */
export const LANDING_STYLES: Record<LandingTemplate, { id: string; label: string }[]> = {
  product: [
    { id: 'packshot', label: 'Packshot (clásico)' },
    { id: 'spotlight', label: 'Spotlight (marca gigante)' },
    { id: 'editorial', label: 'Editorial (lujo)' },
  ],
  service: [
    { id: 'elegant', label: 'Elegante (claro)' },
    { id: 'immersive', label: 'Inmersivo (pantalla completa)' },
    { id: 'poster', label: 'Cartel (título gigante)' },
  ],
  corporate: [
    { id: 'editorial', label: 'Editorial (claim + filosofía)' },
    { id: 'manifesto', label: 'Manifiesto (tipográfico)' },
    { id: 'gallery', label: 'Galería (mosaico)' },
  ],
};

/** A freely-labelled info section on the landing (label + content). */
export interface LandingInfoBlock {
  label: string;
  body: string;
}

/** Product landing activation + template + enrichment (edited in the wizard). */
export interface ProductLanding {
  active: boolean;
  template: LandingTemplate;
  style: string;
  badge: string;
  info_blocks: LandingInfoBlock[];
}

export interface Disparador {
  id: string;
  tipo: 'oferta_ultimo_momento' | 'oferta_llenado_cupo' | 'aviso_cliente' | 'promocion_especial';
  activo: boolean;
  configuracion: Record<string, unknown>;
}

export interface ParametrosCoste {
  costeProduccionVariable: number;
  costeProductoFijo: number;
  costeComercialPet: number;
  costeComercialResto: number;
  margen: number;
  descuentos: number;
  precioTemporada: number;
}

export interface ConfiguracionDescuentos {
  porTemporada: number;
  porVolumen: number;
  porProntoPago: number;
  porFidelizacion: number;
  oferta: number;
  cupon: number;
  otroMotivo: number;
}

export interface AspectosGenerales {
  tipoTemporada: 'siempre' | 'temporada' | '';
  numeroTemporadas: number;
  temporadas: Array<{
    id: string;
    numero: number;
    etiqueta: string;
    color: string;
    rangos: Array<{ id: string; fechaInicio: string; fechaFin: string }>;
  }>;
  duracionDias: number;
  pernoctas: number;
  diaEntrada: boolean;
}

export interface DisponibilidadNegocio {
  tipoApertura: 'todo-año' | 'temporadas' | 'personalizado';
  diasSemana: Record<string, boolean>;
  mesesAño: Record<string, boolean>;
}

export interface Marketing {
  materialPublicitario: {
    online: { activo: boolean; url: string };
    digital: { activo: boolean; archivos: string[] };
    tradicional: { activo: boolean; descripcion: string };
  };
  canales: { principal: string; adicionales: string[] };
  metaSeo: {
    ogTitle: string;
    ogDescription: string;
    metaDescription: string;
    keywords: string;
    targetAudience: string;
    ageRange: string;
    geoLocation: string;
    callToAction: string;
  };
  argumentoVenta: string;
  descripcionMarketing: string;
}

export interface Running {
  activo: boolean;
  perspectivaCliente: string;
  horarioInicio: string;
  horarioFin: string;
  descripcionActividad: string;
  anotacionSingularidades: string;
  actividades: string[];
  serviciosTerapeuticos: string;
  propuestasOcio: string;
  contenidosOnline: string;
  otros: string;
  contingencias: string;
}

export interface Gastro {
  activo: boolean;
  tipoServicio: string;
  logistica: string;
  relevanteElaborada: boolean;
  descripcionElaborada: string;
  alergenos: Record<string, boolean>;
}

export interface Cupo {
  activo: boolean;
  tipoLimite: 'fecha' | 'cantidad' | 'ambos';
  cantidadMaxima: number;
  fechaLimite: string;
  descripcion: string;
}

export interface ClienteObjetivo {
  tipoCliente: string[];
  atributos: string[];
  tamaño: string[];
  atributosValores: Record<string, string>;
}

export interface EstructuraProducto {
  perspectivaCostes: string;
  margenComercial: string;
  margenIndustrial: string;
  iddi: string;
}

// Categorization keyed by MetadataTree.id (global trees like vida_interna… AND
// org-scoped trees like alicun-comercial). Values are MetadataNode ids.
export type Categorization = Record<string, string[]>;

export interface ProductComponent {
  product_id: string;
  quantity: number;
  label: string;
  isOptional: boolean;
  priceOverride: number; // -1 means use source product price
}

export interface PopulatedComponent {
  product_id: string;
  quantity: number;
  label: string;
  isOptional: boolean;
  priceOverride: number;
  effectivePrice: number;
  subtotal: number;
  source: {
    _id: string;
    name: string;
    price: number;
    pvpFinal: number;
    sku: string;
    mainImage: string;
    status: string;
    tipoProducto: string;
  } | null;
}

export interface ComponentsResponse {
  components: PopulatedComponent[];
  totalComponentsPrice: number;
  componentCount: number;
  minComponents: number;
  maxComponents: number;
}

export interface SimpleProductSummary {
  _id: string;
  name: string;
  price: number;
  pvpFinal: number;
  sku: string;
  mainImage: string;
  status: string;
  tipoProducto: string;
}

export type VisibilityLevel = 'private' | 'meetingPoint' | 'network' | 'public';

export const VISIBILITY_LABELS: Record<VisibilityLevel, string> = {
  private: 'Privado',
  meetingPoint: 'Meeting Point',
  network: 'Red',
  public: 'Público',
};

export interface CollaboratorInfo {
  organization_id: string;
  role: 'owner' | 'editor';
  contribution: number;
  label: string;
}

export interface ContributorProduct {
  product_id: string;
  organization_id: string;
  quantity: number;
  label: string;
}

export interface CollaborativeSettings {
  isCollaborative: boolean;
  visibilityLevel: VisibilityLevel;
  collaborators: CollaboratorInfo[];
  contributorProducts: ContributorProduct[];
  minCollaborators: number;
  maxCollaborators: number;
}

export interface PopulatedCollaborator extends CollaboratorInfo {
  orgName: string;
  orgSlug: string;
}

export interface PopulatedContributorProduct extends ContributorProduct {
  orgName: string;
  source: {
    _id: string;
    name: string;
    price: number;
    pvpFinal: number;
    sku: string;
    mainImage: string;
  } | null;
}

export interface CollaborativeResponse {
  collaborators: PopulatedCollaborator[];
  contributorProducts: PopulatedContributorProduct[];
  totalContribution: number;
  visibilityLevel: VisibilityLevel;
  minCollaborators: number;
  maxCollaborators: number;
}

export interface OrganizationSummary {
  _id: string;
  name: string;
  slug: string;
}

export interface VidaProduct {
  _id: string;
  organization_id: string;
  project_id?: string;
  parent_id?: string | null;
  name: string;
  slug: string;
  description: string;
  type: ProductType;
  tipoProducto: TipoProducto | '';
  status: ProductStatus;

  ecommerce_active: boolean;
  catalog_active: boolean;
  // Tejedores (VIDA-297): el tejedor ofrece este producto a las orgs con las que
  // colabora; aparece como opción al montar un producto colaborativo (F5).
  shared_with_network?: boolean;
  landing?: ProductLanding;

  price: number;
  descuento: number;
  iva: number;
  pvpFinal: number;
  sku: string;
  stock: number;
  stockMinimo: number;
  alertaStock: boolean;
  parametrosCoste: ParametrosCoste;
  descuentosPersonalizados: ConfiguracionDescuentos;

  mainImage: string;
  images: ImageWithMetadata[];
  imagenesPorTemporada: Record<string, string>;

  categorization: Categorization;
  tags: string[];

  aspectosGenerales: AspectosGenerales;
  disponibilidadNegocio: DisponibilidadNegocio;
  marketing: Marketing;
  running: Running;
  gastro: Gastro;
  cupo: Cupo;
  clienteObjetivo: ClienteObjetivo;
  estructuraProducto: EstructuraProducto;

  enlaces: EnlaceExterno[];
  disparadores: Disparador[];

  components: ProductComponent[];
  minComponents: number;
  maxComponents: number;
  collaborativeSettings: CollaborativeSettings;

  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProductStats {
  total: number;
  active: number;
  draft: number;
  inactive: number;
  archived: number;
  ecommerce_active: number;
  catalog_active: number;
  composite: number;
  collaborative: number;
  byTipoProducto: Record<string, number>;
}

export interface ProductsListResponse {
  products: VidaProduct[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// === Project types ===

export const PROJECT_STATUSES = ['active', 'inactive', 'archived'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export interface VidaProject {
  _id: string;
  organization_id: string;
  name: string;
  slug: string;
  description: string;
  status: ProjectStatus;
  allowed_nodes: string[];
  assigned_users: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectsListResponse {
  projects: VidaProject[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface MyNodesResponse {
  restricted: boolean;
  allowed_nodes: string[];
  project_count?: number;
}
